import { useMemo, type ReactNode } from 'react';
import { AppShell } from './AppShell';
import { useApp } from './AppContext';
import { BrandedLoading } from './components';
import { Navigate, NavigationProvider, RouteScope, useLocation } from './navigation';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectDetailPage, ProjectsPage, TaskDetailPage, TasksPage } from './pages/WorkPages';
import { PeoplePage } from './pages/PeoplePage';
import { EmployeeProfilePage } from './pages/EmployeeProfilePage';
import { AttendancePage } from './pages/AttendancePage';
import { SchedulePage } from './pages/SchedulePage';
import { CalendarPage } from './pages/CalendarPage';
import { LeavesPage } from './pages/LeavesPage';
import { ApprovalsPage, FinancePage } from './pages/FinancePages';
import { CurrencyFinancePage } from './pages/CurrencyFinancePage';
import { PayrollPage } from './pages/PayrollPage';
import { AnnouncementsPage, ChatPage, MeetingsPage, NotificationsPage, SupportPage } from './pages/SupportPages';
import { AuditPage, CrmPage, MorePage, NotFoundPage, SettingsPage } from './pages/MorePages';
import { PasswordVaultPage } from './pages/PasswordVaultPage';
import { ClientDocumentsPage, PoliciesPage, ReimbursementsPage } from './pages/PortalPages';
import { EmailAccessPage } from './pages/EmailAccessPage';
import { PlatformAdminPage } from './pages/PlatformAdminPage';
import { PasswordSetupPage } from './pages/PasswordSetupPage';
import { FeedbackPage, MealsPage } from './pages/TeamOperationsPages';
import { InstallPage } from './pages/InstallPage';
import { PrivacyPolicyPage, PublicSupportPage } from './pages/PublicLegalPages';
import { DeviceActivityPage } from './pages/DeviceActivityPage';

type Route = { pattern: string; element: ReactNode };

const routes: Route[] = [
  { pattern: '/', element: <DashboardPage /> },
  { pattern: '/tasks', element: <TasksPage /> },
  { pattern: '/tasks/:id', element: <TaskDetailPage /> },
  { pattern: '/projects', element: <ProjectsPage /> },
  { pattern: '/projects/:id', element: <ProjectDetailPage /> },
  { pattern: '/people', element: <PeoplePage /> },
  { pattern: '/people/:id', element: <EmployeeProfilePage /> },
  { pattern: '/profile', element: <EmployeeProfilePage /> },
  { pattern: '/attendance', element: <AttendancePage /> },
  { pattern: '/device-activity', element: <DeviceActivityPage /> },
  { pattern: '/schedule', element: <SchedulePage /> },
  { pattern: '/calendar', element: <CalendarPage /> },
  { pattern: '/leave', element: <LeavesPage /> },
  { pattern: '/finance', element: <FinancePage /> },
  { pattern: '/currency-finance', element: <CurrencyFinancePage /> },
  { pattern: '/payroll', element: <PayrollPage /> },
  { pattern: '/reimbursements', element: <ReimbursementsPage /> },
  { pattern: '/feedback', element: <FeedbackPage /> },
  { pattern: '/meals', element: <MealsPage /> },
  { pattern: '/approvals', element: <ApprovalsPage /> },
  { pattern: '/policies', element: <PoliciesPage /> },
  { pattern: '/documents', element: <ClientDocumentsPage /> },
  { pattern: '/support', element: <SupportPage /> },
  { pattern: '/chat', element: <ChatPage /> },
  { pattern: '/meetings', element: <MeetingsPage /> },
  { pattern: '/announcements', element: <AnnouncementsPage /> },
  { pattern: '/notifications', element: <NotificationsPage /> },
  { pattern: '/email', element: <EmailAccessPage /> },
  { pattern: '/crm', element: <CrmPage /> },
  { pattern: '/vault', element: <PasswordVaultPage /> },
  { pattern: '/more', element: <MorePage /> },
  { pattern: '/settings', element: <SettingsPage /> },
  { pattern: '/audit', element: <AuditPage /> },
];

function matchRoute(pattern: string, pathname: string) {
  const expected = pattern.split('/').filter(Boolean);
  const actual = pathname.split('/').filter(Boolean);
  if (expected.length !== actual.length) return null;
  const params: Record<string, string> = {};
  for (let index = 0; index < expected.length; index += 1) {
    if (expected[index].startsWith(':')) params[expected[index].slice(1)] = decodeURIComponent(actual[index]);
    else if (expected[index] !== actual[index]) return null;
  }
  return params;
}

function Routes() {
  const { bootstrap, loading } = useApp();
  const location = useLocation();
  const match = useMemo(() => {
    for (const route of routes) {
      const params = matchRoute(route.pattern, location.pathname);
      if (params) return { ...route, params };
    }
    return { pattern: '*', element: <NotFoundPage />, params: {} };
  }, [location.pathname]);

  if (location.pathname === '/super-admin' || location.pathname === '/super-admin/login') return <PlatformAdminPage />;
  if (location.pathname === '/install') return <InstallPage />;
  if (['/privacy', '/privacy-policy', '/legal/privacy'].includes(location.pathname)) return <PrivacyPolicyPage />;
  if (loading) return <div className="full-page-state full-page-state--brand"><BrandedLoading /></div>;
  if (location.pathname === '/support' && !bootstrap) return <PublicSupportPage />;
  if (location.pathname === '/login') return bootstrap ? <Navigate to="/" replace /> : <LoginPage />;
  if (!bootstrap) return <Navigate to="/login" replace />;
  if (bootstrap.user.must_change_password) return <PasswordSetupPage />;
  if (location.pathname === '/work') return <Navigate to={`/tasks${location.search}`} replace />;
  const administratorOnly = location.pathname === '/settings' || location.pathname === '/audit'
    || location.pathname === '/people' || location.pathname.startsWith('/people/');
  if (administratorOnly && !['owner', 'admin', 'manager'].includes(bootstrap.user.role)) {
    return <RouteScope params={{}} outlet={<NotFoundPage />}><AppShell /></RouteScope>;
  }
  if (location.pathname === '/device-activity' && !bootstrap.capabilities.manageDeviceActivity) {
    return <RouteScope params={{}} outlet={<NotFoundPage />}><AppShell /></RouteScope>;
  }
  if (location.pathname.startsWith('/finance') && bootstrap.user.role === 'manager') {
    return <RouteScope params={{}} outlet={<NotFoundPage />}><AppShell /></RouteScope>;
  }
  if (location.pathname === '/crm' && !bootstrap.capabilities.canAccessCrm) {
    return <RouteScope params={{}} outlet={<NotFoundPage />}><AppShell /></RouteScope>;
  }
  if (location.pathname === '/currency-finance' && !bootstrap.capabilities.canAccessCurrencyFinance) {
    return <RouteScope params={{}} outlet={<NotFoundPage />}><AppShell /></RouteScope>;
  }
  if ((location.pathname === '/email' || location.pathname === '/feedback' || location.pathname === '/meals') && bootstrap.user.role === 'client') {
    return <RouteScope params={{}} outlet={<NotFoundPage />}><AppShell /></RouteScope>;
  }
  return <RouteScope params={match.params} outlet={match.element}><AppShell /></RouteScope>;
}

export function AppRouter() {
  return <NavigationProvider><Routes /></NavigationProvider>;
}
